import { Stack } from '@mantine/core';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { useTravelRequestCreate } from '@/module/Travel/Request/model/TravelRequestQuery';
import {
  TravelRequestSchema,
  defaultTravelRequestValues,
  toTravelRequestRequest,
  travelRequestSchema,
} from '@/module/Travel/Request/model/TravelRequestSchema';

import { useAppNotification } from '@feature/utils';

import { TravelRequestForm } from './TravelRequestForm';
import { travelProcessNextStepId } from './components/TravelProcessTimeline';

export function TravelRequestCreate() {
  const { t } = useTranslation();
  const { mutate, isPending, isError, isSuccess } = useTravelRequestCreate();

  const notification = useAppNotification();
  const navigate = useNavigate();

  const onSubmit = (values: TravelRequestSchema) => {
    mutate(toTravelRequestRequest(values), {
      onError: notification.error,
      onSuccess: created => {
        notification.success();
        // Back from the saved draft would reopen an empty create form.
        navigate({
          to: '/travel/requests/$requestId',
          params: { requestId: String(created.id) },
          hash: travelProcessNextStepId(created),
          // The view scrolls once its queries settle; the router would scroll earlier, before the layout stops moving.
          hashScrollIntoView: false,
          replace: true,
        });
      },
    });
  };

  return (
    <Stack>
      <TravelRequestForm
        title={t('travel.requests.form.title.create')}
        schema={travelRequestSchema}
        defaultValues={defaultTravelRequestValues}
        values={undefined}
        withRequiredNote
        isSubmitting={isPending}
        isError={isError}
        isSuccess={isSuccess}
        onSubmit={onSubmit}
        onCancel={() => navigate({ to: '..' })}
      />
    </Stack>
  );
}
